// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/Customer.yaml
// Generated: 2025-11-13T17:17:06.999Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { customerService } from '@/services/sales/CustomerService';
import { CreateCustomer, UpdateCustomer, Customer } from '@/types/sales/Customer';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for Customer
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const customerKeys = {
  all: (tenantId?: string | null) => ['customers', tenantId] as const,
  lists: (tenantId?: string | null) => [...customerKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...customerKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...customerKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...customerKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all Customers
 */
export function useCustomers(options?: Omit<UseQueryOptions<Customer[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<Customer[], Error>({
    queryKey: customerKeys.lists(tenantId),
    queryFn: () => customerService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single Customer by ID
 */
export function useCustomer(
  id: string,
  options?: Omit<UseQueryOptions<Customer | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<Customer | undefined, Error>({
    queryKey: customerKeys.detail(id, tenantId),
    queryFn: () => customerService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new Customer
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<Customer, Error, CreateCustomer, { previousCustomers?: Customer[] }>({
    mutationFn: (data: CreateCustomer) => customerService.create(data),
    onMutate: async (newCustomer) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: customerKeys.lists(tenantId) });

      // Snapshot previous value
      const previousCustomers = queryClient.getQueryData<Customer[]>(customerKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<Customer[]>(customerKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newCustomer, id: (newCustomer as { id?: string }).id ?? 'temp-' + Date.now() } as Customer
      ]);

      return { previousCustomers };
    },
    onError: (_err, _newCustomer, context) => {
      // Rollback on error
      if (context?.previousCustomers) {
        queryClient.setQueryData(customerKeys.lists(tenantId), context.previousCustomers);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: customerKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing Customer
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<Customer, Error, { id: string; data: UpdateCustomer }, { previousCustomer?: Customer; previousCustomers?: Customer[] }>({
    mutationFn: ({ id, data }) => customerService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: customerKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: customerKeys.lists(tenantId) });

      // Snapshot previous values
      const previousCustomer = queryClient.getQueryData<Customer>(customerKeys.detail(id, tenantId));
      const previousCustomers = queryClient.getQueryData<Customer[]>(customerKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<Customer>(customerKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as Customer);

      // Optimistically update list
      queryClient.setQueryData<Customer[]>(customerKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as Customer : item))
      );

      return { previousCustomer, previousCustomers };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousCustomer) {
        queryClient.setQueryData(customerKeys.detail(id, tenantId), context.previousCustomer);
      }
      if (context?.previousCustomers) {
        queryClient.setQueryData(customerKeys.lists(tenantId), context.previousCustomers);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: customerKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a Customer
 */
export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousCustomers?: Customer[] }>({
    mutationFn: (id: string) => customerService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: customerKeys.lists(tenantId) });

      // Snapshot previous value
      const previousCustomers = queryClient.getQueryData<Customer[]>(customerKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<Customer[]>(customerKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousCustomers };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousCustomers) {
        queryClient.setQueryData(customerKeys.lists(tenantId), context.previousCustomers);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: customerKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveCustomer() {
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();

  return {
    mutate: (params: { id?: string; data: CreateCustomer | UpdateCustomer }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateCustomer }, options);
      } else {
        return createMutation.mutate(params.data as CreateCustomer, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
